import './index.less';

import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { FC, lazy, Suspense, useEffect, useState } from 'react';

import CurrencyOfflineModal from '@/components/CurrencyOfflineModal';
import useTransfer from '@/hooks/useTransfer';
import { getExchangeRate } from '@/services/getExchangeRate';
import { UserInfo } from '@/services/types/common';

import { TotalBalance } from './comps/TotalBalance';
import { useTotalBalance } from './comps/TotalBalance/useTotalBalance';
import { useTransferAssets } from './comps/TransferAssets';
import { useSubAccount } from './useSubAccount';

const antIcon = <LoadingOutlined style={{ fontSize: 48 }} spin />;

const SubAccountList = lazy(
  () => import('./comps/SubAccountList/SubAccountList'),
);

const SubAccount: FC = () => {
  const {
    profile,
    currencyCode, // 如果用户设置的法币已下线，currencyCode === 'USD'，否则为用户设置的法币
    pagination,
    getDataSource,
    msgError,
    userInfo,
    hasSubAccount,
    tableLoading,
    dataSource,
    eachChildContractBalance,
    showCurrencyOfflineModal,
    isExchangeTeam,
    bindRequestCount,
  } = useSubAccount();

  const { showTransferModal } = useTransfer();

  const { MemorizedTitle } = useTransferAssets();

  const { updatedTotalBalance, totalBalance, contractTotalBalance } =
    useTotalBalance();

  const [exchangeRate, setExchangeRate] = useState('1');

  useEffect(() => {
    if ((userInfo as UserInfo)?.is_submember) {
      window.location.href = '/app/user/account-info';
    }
  }, [userInfo]);

  /**切换子账号 */
  const [switchListChecked, setSwitchListChecked] = useState(false);
  // 搜索子账号
  const [searchAccountName, setSearchAccountName] = useState('');

  const getAccountList = (args: {
    page?: number;
    pageSize?: number;
    switch?: number;
    memberName?: string;
  }) => {
    const params = {
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      switch: switchListChecked ? 1 : 0,
      memberName: searchAccountName,
    };
    getDataSource({ ...params, ...args });
  };

  useEffect(() => {
    getExchangeRate({ name: currencyCode })
      .then((res) => {
        setExchangeRate((res as string) || '1');
      })
      .catch(() => {
        setExchangeRate('1');
      });
  }, [currencyCode]);

  const refreshTable = (
    type?: string,
    action?: {
      switchListChecked: boolean;
      changeRowSwitch: (cb: () => void) => void;
    },
    page?: number,
  ) => {
    if (type) {
      const cb = () => {
        getAccountList({ switch: switchListChecked ? 1 : 0 });
      };
      if (action) {
        // 修改row popover内快速切换状态
        if (action.changeRowSwitch) {
          action.changeRowSwitch(cb);
        } else if (action.switchListChecked) {
          // 仅展示允许切换的子账号
          getAccountList({ switch: 1 });
        } else {
          // 展示所有子账号
          getAccountList({ switch: 0 });
        }
      }
    } else {
      setSearchAccountName('');
      getAccountList({
        page: page || pagination.current || 1,
        switch: 0,
        memberName: '',
      });
    }
  };

  const onSearchSubAccount = (memberName: string) => {
    getAccountList({
      memberName,
    });
  };

  return (
    <div style={{ height: '100%' }}>
      <TotalBalance
        profile={profile}
        currencyCode={currencyCode}
        refreshTable={refreshTable}
        setSwitchListChecked={setSwitchListChecked}
        msgError={msgError}
        exchangeRate={exchangeRate}
        isExchangeTeam={isExchangeTeam}
        bindRequestCount={bindRequestCount}
        submemberList={dataSource}
        totalBalance={totalBalance}
        contractTotalBalance={contractTotalBalance}
        onTransfer={() => {
          showTransferModal({
            fromUid: userInfo.id,
            toUid: '',
            transferType: 2,
            onSuccess: () => {
              updatedTotalBalance();
              refreshTable();
            },
            defaultFrom: 'ACCOUNT_TYPE_FUND',
            coin: 'USDT',
          });
        }}
      />
      <div className="sub-account-list-card">
        <MemorizedTitle
          hasSubAccount={hasSubAccount}
          refreshTable={refreshTable}
          switchListChecked={switchListChecked}
          setSwitchListChecked={setSwitchListChecked}
          setSearchAccountName={setSearchAccountName}
          onSearchSubAccount={onSearchSubAccount}
        />
        <div className="sub-account-list-body">
          <Suspense
            fallback={
              <div className="sub-account-list-body-spin">
                <Spin indicator={antIcon} />
              </div>
            }
          >
            <SubAccountList
              getAccountList={getAccountList}
              refreshTable={refreshTable}
              exchangeRate={exchangeRate}
              currencyCode={currencyCode}
              pagination={pagination}
              tableLoading={tableLoading}
              dataSource={dataSource}
              eachChildContractBalance={eachChildContractBalance}
              updatedTotalBalance={updatedTotalBalance}
            />
          </Suspense>
        </div>
      </div>
      <CurrencyOfflineModal code={showCurrencyOfflineModal} />
    </div>
  );
};
export default SubAccount;
